<script setup>
// Renders a third-party embed (Google Form / Map / YouTube) in a framed iframe.
// The backend (pages/blocks.py) restricts `url` to an allowlisted host, so the
// src here is already trusted. A `height` => fixed-height frame (good for forms,
// which the iframe scrolls internally); no height => 16:9 responsive (video/maps).
// `title` is the iframe's accessibility label only — there's no visible chrome
// heading; add a rich_text/hero block before the embed if a title is wanted.
const props = defineProps({ data: { type: Object, required: true } });

const hasFixedHeight = computed(
  () => typeof props.data.height === "number" && props.data.height > 0,
);

// Google Forms only render in embed chrome when the URL carries ?embedded=true;
// other hosts (maps/youtube) already use their own embed URL form, so pass through.
const embedSrc = computed(() => {
  try {
    const u = new URL(props.data.url);
    if (u.hostname === "docs.google.com" && u.pathname.includes("/forms/")) {
      u.searchParams.set("embedded", "true");
      return u.toString();
    }
    return props.data.url;
  } catch {
    return props.data.url;
  }
});
</script>

<template>
  <section class="py-10 md:py-14">
    <div class="container-x">
      <div class="mx-auto max-w-3xl">
        <!-- Fixed-height frame (forms): the iframe shows its own scrollbar if taller. -->
        <div
          v-if="hasFixedHeight"
          class="overflow-hidden rounded-2xl border border-ink/10 bg-paper shadow-sm"
        >
          <iframe
            :src="embedSrc"
            :title="data.title || '嵌入內容'"
            :height="data.height"
            width="100%"
            loading="lazy"
            referrerpolicy="no-referrer-when-downgrade"
            class="block w-full"
          >載入中…</iframe>
        </div>

        <!-- Responsive 16:9 frame (video / maps). -->
        <div
          v-else
          class="relative overflow-hidden rounded-2xl border border-ink/10 bg-paper shadow-sm"
          style="aspect-ratio: 16 / 9"
        >
          <iframe
            :src="embedSrc"
            :title="data.title || '嵌入內容'"
            loading="lazy"
            referrerpolicy="no-referrer-when-downgrade"
            allowfullscreen
            class="absolute inset-0 h-full w-full"
          >載入中…</iframe>
        </div>

        <p class="mt-3 text-center text-sm text-muted">
          無法顯示？
          <a
            :href="data.url"
            target="_blank"
            rel="noopener noreferrer"
            class="text-brand-green underline-offset-2 hover:text-brand-orange hover:underline"
          >在新分頁開啟</a>
        </p>
      </div>
    </div>
  </section>
</template>
